/**
 * SL-15 round-9 @codex F2 — the FULL 3D-frame transform for a direct EDIT
 * (MOVE / COPY / ROTATE / SCALE / MIRROR) of an ELLIPSE.
 *
 * Extracted from `geometryTransforms.ts` to keep that file under the 2000-line
 * `max-lines` ceiling (CLAUDE.md); its `transformEntityCoords` Ellipse arm calls
 * {@link ellipseEditFrame} and spreads the result — `{ ...entity, ...frame }` —
 * exactly like the Spline arm spreads `spreadSplineExtrusion`, so the external
 * import path (`transformEntityCoords`) is unchanged.
 *
 * An ELLIPSE has a full 3D frame: `center` (position + z ELEVATION),
 * `majorAxisEndpoint` (a DIRECTION vector whose z tilts the plane), the
 * minor/major `ratio`, and the OCS `extrusion` normal. A pure TRANSLATION leaves
 * the major axis, ratio, normal and params INVARIANT (center-only, byte-identical
 * — no Rytz float noise); a ROTATE/SCALE/MIRROR (non-identity linear part)
 * transforms the WHOLE frame through the ONE-TRUTH
 * `computeExplodedEllipseGeometry`, IDENTICAL to the block-explode path (center
 * incl. z, 3D major axis, non-uniform-conjugate ratio + Rytz principal-axis
 * recovery, OCS normal via the cofactor map, arc-param re-anchor). Pre-fix the
 * Ellipse arm moved ONLY the center and carried the major axis + normal VERBATIM,
 * so a ROTATE/MIRROR/SCALE of an elevated / tilted ellipse left its orientation +
 * tilt STALE (and neither `rotateEntityAngles` nor `scaleEntityRadii` has an
 * Ellipse arm to compensate).
 *
 * Reference: ezdxf `ConstructionEllipse.transform` (`minor = extrusion × major`,
 * plane normal via the cofactor map, Rytz when the images are non-orthogonal).
 */
import type { EllipseEntity } from "@dxf-ai-editor/drawing-model";

import { probeAffine2d } from "./affine2dProbe";
import {
  computeExplodedEllipseGeometry,
  type ExplodeAffineMatrix,
} from "./ellipseExplodeGeometry";

type Point2D = { x: number; y: number };
type TransformFn = (p: Point2D) => Point2D;

/** The implicit OCS extrusion normal of an ellipse that OMITS 210/220/230. */
const DEFAULT_EXTRUSION_NORMAL = { x: 0, y: 0, z: 1 };

/**
 * The ellipse fields a direct edit rewrites — spread over `...entity` by the
 * caller. The identity (translation) branch returns ONLY `{ center }` so the
 * spread leaves the major axis / ratio / normal / params untouched (a
 * spread never sets an absent key), preserving byte-identical MOVE/COPY output.
 */
interface EllipseEditFrame {
  center: EllipseEntity["center"];
  majorAxisEndpoint?: EllipseEntity["majorAxisEndpoint"];
  ratio?: number;
  startParam?: number;
  endParam?: number;
  extrusion?: EllipseEntity["extrusion"];
}

/**
 * Compute the transformed ellipse frame for the 2D edit `fn`. The affine
 * (linear part `a,b,c,d` + translation `e,f`) comes from {@link probeAffine2d},
 * this package's ONE three-probe affine reader — the same one the OLE2FRAME
 * refusal and `geometryTransforms.ts`'s `linearPart` read.
 */
export function ellipseEditFrame(
  entity: EllipseEntity,
  fn: TransformFn,
): EllipseEditFrame {
  const { a, b, c, d, e, f } = probeAffine2d(fn);

  if (a === 1 && b === 0 && c === 0 && d === 1) {
    // Pure TRANSLATION (identity linear part) — only the center position moves;
    // preserve the optional z ELEVATION (a planar ellipse stays 2D, no spurious
    // z:0). The major-axis vector, ratio, normal and params are invariant, so we
    // return NO override for them (byte-identical to the pre-fix center-only path).
    const moved = fn(entity.center);
    const center =
      entity.center.z !== undefined
        ? { ...entity.center, x: moved.x, y: moved.y, z: entity.center.z }
        : { ...entity.center, x: moved.x, y: moved.y };
    return { center };
  }

  // Non-identity linear part (ROTATE / SCALE / MIRROR) — transform the FULL frame.
  // The app's 2D-plane edits leave z INVARIANT (`scaleZ` = 1, no INSERT elevation).
  const matrix: ExplodeAffineMatrix = { a, b, c, d, e, f };
  const transformed = computeExplodedEllipseGeometry(
    {
      center: entity.center,
      majorAxisEndpoint: entity.majorAxisEndpoint,
      ratio: entity.ratio,
      // `extrusion` is a REQUIRED schema default (0,0,1) but may be `undefined` at
      // runtime on an Ellipse built without Zod (block-explode promotions) —
      // default it so the frame transform never reads off `undefined`.
      extrusion: entity.extrusion ?? DEFAULT_EXTRUSION_NORMAL,
      startParam: entity.startParam,
      endParam: entity.endParam,
    },
    { matrix, scaleZ: 1, insertionZ: 0, basePointZ: 0 },
  );
  return {
    center: transformed.center,
    majorAxisEndpoint: transformed.majorAxisEndpoint,
    ratio: transformed.ratio,
    // Re-anchored params (byte-identical for a full ellipse / frame-preserving
    // edit; the `??` guards the helper's params-absent contract).
    startParam: transformed.startParam ?? entity.startParam,
    endParam: transformed.endParam ?? entity.endParam,
    // The transformed OCS normal — ROTATE rotates its XY, MIRROR flips it to
    // (0,0,−1) (the handedness the mirrored plane needs), an orientation-preserving
    // edit collapses it back to the (0,0,1) canonical default.
    extrusion: transformed.extrusion,
  };
}
